<template>
  <div id="container">
    <div
      id="bu__tabulator-fields"
      ref="table"
      onContextMenu="return(false)"
    />
    <button
      v-if="table && table.getDataCount() < maxRowCount"
      type="button"
      class="controls__button controls__button_add_row"
      @click="addRow"
    >
      <base-material-design-icon
        icon="plus"
        class="controls__icon"
      />
      <span>Add Row</span>
    </button>
    <base-material-design-icon
      ref="icon"
      style="font-size:14px;"
      icon="closeCircle"
    />
  </div>
</template>

<script>

import { mapActions, mapState } from 'vuex'
import Tabulator from 'tabulator-tables'
import BaseModalMixins from '@/common/BaseModalMixins'

const valuePlaceholder = '- value -'

export default {
  name: 'BaseBulkUpdateFields',
  mixins: [BaseModalMixins],
  props: {
    columns: {
        type: Array,
        required: true,
        validator (value) { 
            // Expects an Array of Objects - see features/links/Links.vue for original example
            // Should accept any Tabulator Column Component property
            // {
            //   title: String (required),
            //   options: Array (required),
            // }
            return value.every(obj => obj.title)
        }
    }
  },
  data () {
    const sortedDefaultFieldLabels = this.columns.map(column => {
      return {
        [this.getFieldNameFromTitle(column.title)]: column.title
      }
    })
    return {
      table: null,
      rowId: 0,
      rawIconHtmlString: '',
      fieldLabels: sortedDefaultFieldLabels.reduce((acc, cur) => {
        const key = Object.keys(cur)[0]
        acc[key] = cur[key]
        return acc
      }, {}),
      fields: sortedDefaultFieldLabels.map(obj => {
        const key = Object.keys(obj)[0]
        return {
          label: obj[key],
          value: key
        }
      })
    }
  },
  computed: {
    ...mapState('urlCensus', [
      'pageTypes',
      'urlStatuses',
      'sections',
      'contentBriefStatuses',
      'writingStatuses',
    ]),
    ...mapState('users', ['users']),
    maxRowCount () {
      return this.columns.length 
    },
    values () {
      return this.columns.reduce((previous, current) => {
        previous[this.getFieldNameFromTitle(current.title)] = current.options
        return previous
      }, {})
    }
  },
  methods: {
    getFieldNameFromTitle (title) {
      return title.toLowerCase().split(' ').join('_')
    },
    addRow () {
      if (this.table && this.table.getDataCount() < this.maxRowCount) {
        this.rowId += 1
        this.table.addRow({ id: this.rowId, close_button: 1, field: this.fields[0].value, value: valuePlaceholder })
        this.table.scrollToRow(this.rowId)
      }
    },
    objectToSelectList (obj) {
      const selectList = Object.keys(obj)
        .filter(key => key !== '-1')
        .map(key => ({
          label: obj[key],
          value: key
        })).sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase()) ? 1 : -1)
      if (-1 in obj) {
        selectList.unshift({
          label: obj[-1],
          value: -1
        })
      }
      return selectList
    }
  },
  mounted () {
    this.rawIconHtmlString = this.$refs.icon.$el.outerHTML.toString()
    this.$refs.icon.$el.parentNode.removeChild(this.$refs.icon.$el)

    const vueComponent = this
    this.table = new Tabulator(this.$refs.table, {
      data: [{ field: vueComponent.fields[0].value, value: valuePlaceholder }],
      layout: 'fitColumns',
      movableColumns: false,
      headerVisible: false,
      cellVertAlign: 'middle',
      maxHeight: 170,
      dataChanged (data) {
        data = data.filter(row => row.field && row.value && row.value !== valuePlaceholder)
        data = data.map(row => ({ field: row.field, value: row.value }))
        if (data && data.length) {
          vueComponent.$emit('fieldData', data)
        }
      },
      columns: [
        {
          title: '',
          field: 'close_button',
          hozAlign: 'center',
          resizable: false,
          headerSort: false,
          minWidth: 50,
          width: 50,
          formatter: function (cell, formatterParams, onRendered) { // plain text value
            return vueComponent.rawIconHtmlString // this provides material-design-icon as raw html
          },
          cellClick: function (e, cell) {
            const rowCount = cell.getTable().getDataCount()
            const row = cell.getRow()
            if (rowCount === 1) {
              row.getCell('value').setValue(valuePlaceholder)
            } else {
              row.delete()
            }
          }
        },
        {
          title: 'Field',
          field: 'field',
          resizable: false,
          width: 150,
          editor: 'select',
          editorParams: {
            showListOnEmpty: true,
            values: vueComponent.fields
          },
          formatter (cell) {
            const value = cell.getValue()
            return vueComponent.fieldLabels[value]
          },
          cellEdited (cell) {
            cell.getRow().getCell('value').setValue(valuePlaceholder)
          }
        },
        {
          title: 'Value',
          field: 'value',
          resizable: false,
          headerSort: false,
          minWidth: 100,
          cellEditing (cell) {
            if (cell.getValue() === valuePlaceholder) {
              cell.setValue('')
            }
          },
          editor: 'autocomplete',
          editorParams (cell) {
            const editorParams = {
              showListOnEmpty: true
            }
            const field = cell.getRow().getCell('field').getValue()
            let values = vueComponent.values[field]
            editorParams.values = values
            return editorParams
          },
          formatter (cell) {
            const field = cell.getRow().getCell('field')
            const value = cell.getValue()
            if (!value) {
              return valuePlaceholder
            }
            if (field) {
              const fieldValue = field.getValue()
              if (vueComponent.values[fieldValue]) {
                return vueComponent.columns.find(column => {
                  return vueComponent.getFieldNameFromTitle(column.title) === fieldValue
                }).options.find(option => {
                  return option.value === value
                })?.label ?? value
              }
            }
            return valuePlaceholder
          }
        }
      ]

    })
  }
}

</script>

<style>
#bu__tabulator-fields .tabulator-cell {
  height: 40px;
}
</style>

<style scoped lang="scss">
  #container {
    margin-top: 5px;
  }
  .controls {
    display: flex;
    margin-top: 30px;

    &__icon {
      margin-right: 2px;
      font-size: 15px;
    }

    &__button {
      height: 40px;
      width: 15%;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      border: none;
      outline: none;
      cursor: pointer;

      color: $rfx-dark-march;
      background-color: #fff;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;
      }

      &_add_row {
        width: 100%;
      }

      &_cancel {
        margin-left: auto;
        margin-right: 10px;
      }
    }
  }

</style>
